import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { AnimatePresence, motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faLightbulb,
  faPeopleGroup,
  faSeedling,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

import { useScrollAnimation } from "../../../../utils/Utils";
import ugadiCelebration from "../../../../Assets/LifeAtKapilTech/GroupImage_2.png";
import teamBirthday from "../../../../Assets/LifeAtKapilTech/GroupImage_1.png";
import cricketNight from "../../../../Assets/LifeAtKapilTech/Cricket_Game.png";
import pickleballChampions from "../../../../Assets/LifeAtKapilTech/PickleBall_Game.png";
import holiCelebration from "../../../../Assets/LifeAtKapilTech/Holi_Event.png";
import newYearLunch from "../../../../Assets/LifeAtKapilTech/NewYear_Lunch.png";

const COLORS = {
  ink: "#16181d",
  subink: "#4b5563",
  faint: "#9ca3af",
  line: "#e6e8eb",
  panel: "#ffffff",
  canvas: "#f6f7f9",
  accent: "#001534",
  accentSoft: "#eaf1ee",
};

const PILLARS = [
  {
    icon: faSeedling,
    image: ugadiCelebration,
    title: "Festive Traditions",
    text: "From Ugadi to Holi, we bring our culture into the office and celebrate together as one team.",
  },
  {
    icon: faPeopleGroup,
    image: cricketNight,
    title: "Team Bonding",
    text: "From late-night nets to weekend matches, our cricket squad never turns down a game — or a chance to talk some trash about it.",
  },
  {
    icon: faLightbulb,
    image: teamBirthday,
    title: "Genuine Camaraderie",
    text: "Someone's birthday is always the perfect excuse for the whole team to pull up a chair and celebrate together.",
  },
];

const GALLERY = [
  {
    src: newYearLunch,
    caption: "New Year Team Lunch",
    description:
      "We kicked off the year the way we like to do everything — together. A long lunch, good food, and the whole team in one room to toast to what's ahead.",
  },
  {
    src: pickleballChampions,
    caption: "Pickleball Champions",
    description:
      "Turns out our competitive streak doesn't stay in the office. Our pickleball squad took their game to the court — trophies, banners, and bragging rights included.",
  },
];

function Lightbox({ index, onClose, onPrev, onNext }) {
  const item = GALLERY[index];

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft") onPrev();
      if (event.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);

  return (
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-label={item.caption}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 px-4 py-8 sm:px-10"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 sm:right-6 sm:top-6"
      >
        <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
      </button>

      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onPrev();
        }}
        aria-label="Previous image"
        className="absolute left-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 sm:left-6"
      >
        <FontAwesomeIcon icon={faChevronLeft} className="h-4 w-4" />
      </button>

      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onNext();
        }}
        aria-label="Next image"
        className="absolute right-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 sm:right-6"
      >
        <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4" />
      </button>

      <motion.figure
        key={item.src}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={{ duration: 0.2 }}
        className="max-h-full max-w-3xl"
        onClick={(event) => event.stopPropagation()}
      >
        <img
          src={item.src}
          alt={item.caption}
          className="max-h-[75vh] w-full rounded-xl object-contain"
        />
        <figcaption className="mt-4 text-center text-white">
          <span className="block text-base font-semibold">{item.caption}</span>
          <span className="mt-1 block text-sm text-white/70">{item.description}</span>
        </figcaption>
      </motion.figure>
    </motion.div>
  );
}

export default function LifeAtKapilTech() {
  useScrollAnimation();
  const [activeIndex, setActiveIndex] = useState(null);

  const closeLightbox = () => setActiveIndex(null);
  const showPrev = () => setActiveIndex((current) => (current - 1 + GALLERY.length) % GALLERY.length);
  const showNext = () => setActiveIndex((current) => (current + 1) % GALLERY.length);

  return (
    <>
      <Helmet>
        <title>Life at KapilTech | Careers | Kapil Technologies</title>
        <meta
          name="description"
          content="Take a look inside Kapil Technologies — festivals, team celebrations, sports tournaments, and the everyday moments that make up life at KapilTech."
        />
        <meta property="og:title" content="Life at KapilTech | Kapil Technologies" />
        <meta
          property="og:description"
          content="Festivals, team celebrations, and sports tournaments — see what life at Kapil Technologies looks like."
        />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://kapiltech.com/careers/life-at-kapiltech" />
      </Helmet>

      <main style={{ backgroundColor: COLORS.panel }}>
        {/* Hero */}
        <section className="relative flex min-h-[70vh] items-end overflow-hidden sm:min-h-[80vh]">
          <img
            src={holiCelebration}
            alt="KapilTech team celebrating Holi in the office"
            className="absolute h-full w-full object-cover"
            style={{ filter: "brightness(1.15) saturate(1.1)" }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(0,7,20,0.25) 0%, rgba(0,7,20,0.05) 45%, rgba(0,7,20,0.85) 100%)",
            }}
            aria-hidden="true"
          />
          <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-4 pt-32 sm:px-8 sm:pb-10">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-white">
              Careers · Kapil Technologies
            </p>
            <h1 className="max-w-2xl text-[34px] font-semibold leading-tight text-white sm:text-[52px]">
              Life at KapilTech
            </h1>
            <p className="mt-4 max-w-6xl text-sm leading-6 text-white sm:text-base">
              Where work feels like a celebration and colleagues feel like family — a glimpse into
              our festivals, our games, and everything in between.
            </p>
          </div>
        </section>

        {/* Culture pillars */}
        <section className="px-5 py-14 sm:px-8 sm:py-20" style={{ backgroundColor: COLORS.canvas }}>
          <div className="mx-auto max-w-6xl">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-[26px] font-semibold leading-tight sm:text-[32px]" style={{ color: COLORS.ink }}>
                Our Culture
              </h2>
              <p className="mt-3 text-sm leading-6 sm:text-base" style={{ color: COLORS.subink }}>
                A team that works hard together, and celebrates just as hard.
              </p>
            </div>

            <div className="mt-10 grid gap-6 sm:grid-cols-3">
              {PILLARS.map((pillar) => (
                <div
                  key={pillar.title}
                  className="animate-up group overflow-hidden rounded-2xl border"
                  style={{ backgroundColor: COLORS.panel, borderColor: COLORS.line }}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={pillar.image}
                      alt={pillar.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                    />
                    <div
                      className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 via-black/0 to-black/0"
                      aria-hidden="true"
                    />
                    <span
                      className="absolute bottom-4 left-4 flex h-11 w-11 items-center justify-center rounded-full shadow-sm"
                      style={{ backgroundColor: COLORS.panel, color: COLORS.accent }}
                    >
                      <FontAwesomeIcon icon={pillar.icon} className="h-4 w-4" />
                    </span>
                  </div>
                  <div className="p-6">
                    <h3 className="text-base font-semibold" style={{ color: COLORS.ink }}>
                      {pillar.title}
                    </h3>
                    <p className="mt-2 text-sm leading-6" style={{ color: COLORS.subink }}>
                      {pillar.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Gallery */}
        <section className="px-5 py-14 sm:px-8 sm:py-20">
          <div className="mx-auto max-w-6xl">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-[26px] font-semibold leading-tight sm:text-[32px]" style={{ color: COLORS.ink }}>
                Moments That Matter
              </h2>
              <p className="mt-3 text-sm leading-6 sm:text-base" style={{ color: COLORS.subink }}>
                A few snapshots from our festivals, games, and get-togethers.
              </p>
            </div>

            <div className="mt-12 flex flex-col gap-12 sm:gap-16">
              {GALLERY.map((item, index) => (
                <div
                  key={item.caption}
                  className={`animate-up flex flex-col items-center gap-6 sm:gap-10 ${
                    index % 2 === 1 ? "sm:flex-row-reverse" : "sm:flex-row"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setActiveIndex(index)}
                    className="group relative block w-full overflow-hidden rounded-2xl focus:outline-none focus:ring-2 focus:ring-offset-2 sm:w-1/2"
                    style={{ "--tw-ring-color": COLORS.accent }}
                  >
                    <img
                      src={item.src}
                      alt={item.caption}
                      loading="lazy"
                      className="h-72 w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105 sm:h-96"
                    />
                  </button>
                  <div className="w-full sm:w-1/2">
                    <h3 className="text-xl font-semibold sm:text-2xl" style={{ color: COLORS.ink }}>
                      {item.caption}
                    </h3>
                    <p className="mt-3 text-sm leading-6 sm:text-base" style={{ color: COLORS.subink }}>
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative overflow-hidden px-5 py-16 text-center sm:px-8 sm:py-24 mb-20">
          <img
            src={pickleballChampions}
            alt="KapilTech team at a pickleball tournament"
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(0,7,20,0.55) 0%, rgba(0,21,52,0.78) 100%)",
            }}
            aria-hidden="true"
          />
          <div className="relative z-10 mx-auto max-w-2xl">
            <h2 className="text-[26px] font-semibold leading-tight text-white sm:text-[32px]">
              Want to be part of our story?
            </h2>
            <p className="mt-3 text-sm leading-6 text-white/80 sm:text-base">
              We're always looking for people who bring energy, curiosity, and a good sense of humor.
            </p>
            <Link
              to="/careers/open-positions"
              className="mt-7 inline-flex items-center justify-center rounded-md bg-white px-6 py-3 text-sm font-semibold transition-opacity hover:opacity-90"
              style={{ color: COLORS.accent }}
            >
              View Open Positions
            </Link>
          </div>
        </section>
      </main>

      <AnimatePresence>
        {activeIndex !== null && (
          <Lightbox index={activeIndex} onClose={closeLightbox} onPrev={showPrev} onNext={showNext} />
        )}
      </AnimatePresence>
    </>
  );
}
